// Slider Management
import { SLIDER_CONFIGS, getRenderParams } from './slider_mappings.js';
import { PreviewBox } from './preview_box.js';
import RenderService from './render_service.js';

export class SliderManager {
  constructor(stateManager, photoManager) {
    this.stateManager = stateManager;
    this.photoManager = photoManager;
    this.editorState = stateManager.getEditorState();
    this.viewManager = null; // Will be set by EditorApp
    this.saveDebounceTimers = {};
    this.activeSlider = null; // Track which slider is currently active
    
    // PreviewBox instance
    this.previewBox = new PreviewBox(this, stateManager);
    
    // Debounce timer for render settings
    this.renderSettingsDebounceTimer = null;
    
    // Use slider configurations from slider_mappings.js
    this.sliders = SLIDER_CONFIGS;
    
    // Active render for main photo
    this.mainPhotoRender = {
      cancelFn: null,
      photoId: null
    };
    
    // Subscribe to state changes
    this.setupStateListeners();
  }
  
  /**
   * Setup listeners for state changes
   */
  setupStateListeners() {
    // When photo changes, update slider UI
    this.editorState.addEventListener('photoChanged', (e) => {
      const { photoId, photo } = e.detail;
      if (photoId && photo) {
        this.renderSliders(photo);
        
        // Cancel any active render for the old photo
        if (this.mainPhotoRender.cancelFn && this.mainPhotoRender.photoId !== photoId) {
          this.mainPhotoRender.cancelFn();
          this.mainPhotoRender.cancelFn = null;
          this.mainPhotoRender.photoId = null;
          this.hideMainPhotoLoadingOverlay();
        }
      }
    });
    
    // When sliders are loaded (photo switch), update UI
    this.editorState.addEventListener('slidersLoaded', (e) => {
      this.updateSliderUI(e.detail.sliders);
    });
    
    // When page changes, cancel any active render if leaving Edit page
    this.editorState.addEventListener('currentPage', (e) => {
      const newPage = e.detail.value;
      if (newPage !== 'edit' && this.mainPhotoRender.cancelFn) {
        this.mainPhotoRender.cancelFn();
        this.mainPhotoRender.cancelFn = null;
        this.mainPhotoRender.photoId = null;
        this.hideMainPhotoLoadingOverlay();
      }
    });
    
    // Note: Render triggers on slider/settings changes are now handled by ViewManager
    // which calls this.renderMainPhoto(photo) as needed. This avoids duplicate renders
    // and keeps the rendering logic centralized.
  }

  /**
   * Render all sliders for the current photo
   */
  renderSliders(photo) {
    const container = document.getElementById('sliders-container');
    if (!container) return;

    container.innerHTML = '';

    if (!photo) {
      container.innerHTML = '<p class="text-gray-500 text-sm">Select a photo to edit</p>';
      return;
    }

    // Note: We do NOT load inference_steps or seed from the photo.
    // These are global settings controlled by the Account menu, not per-photo settings.
    // The photo's saved inference_steps is only used when rendering the main photo,
    // but for preview renders we always use the current state values.

    // Add style selector first
    const styleSelector = this.createStyleSelector(photo);
    container.appendChild(styleSelector);

    // Add divider
    const divider = document.createElement('div');
    divider.className = 'border-t border-gray-700 my-4';
    container.appendChild(divider);

    // Add sliders - use values from state
    this.sliders.forEach(config => {
      const sliderControl = this.createSliderControl(photo, config);
      container.appendChild(sliderControl);
    });
  }
  
  /**
   * Update slider UI elements with new values (without full re-render)
   */
  updateSliderUI(sliders) {
    this.sliders.forEach(config => {
      const value = sliders[config.name];
      if (value === undefined) return;
      
      const rangeInput = document.querySelector(`.slider-input[data-slider="${config.name}"]`);
      const valueInput = document.querySelector(`.slider-value-input[data-slider="${config.name}"]`);
      
      if (rangeInput) rangeInput.value = value;
      if (valueInput) valueInput.value = value.toFixed(1);
      
      // Update gradient
      if (rangeInput) {
        const hasZeroCenter = config.min < 0 && config.max > 0;
        let gradientStyle;
        
        if (hasZeroCenter) {
          const zeroPercent = (0 - config.min) / (config.max - config.min) * 100;
          const valuePercent = (value - config.min) / (config.max - config.min) * 100;
          
          if (value >= 0) {
            gradientStyle = `linear-gradient(to right, #374151 0%, #374151 ${zeroPercent}%, #3b82f6 ${zeroPercent}%, #3b82f6 ${valuePercent}%, #374151 ${valuePercent}%, #374151 100%)`;
          } else {
            gradientStyle = `linear-gradient(to right, #374151 0%, #374151 ${valuePercent}%, #3b82f6 ${valuePercent}%, #3b82f6 ${zeroPercent}%, #374151 ${zeroPercent}%, #374151 100%)`;
          }
        } else {
          const valuePercent = (value - config.min) / (config.max - config.min) * 100;
          gradientStyle = `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${valuePercent}%, #374151 ${valuePercent}%, #374151 100%)`;
        }
        
        rangeInput.style.background = gradientStyle;
      }
    });
  }

  /**
   * Create style selector dropdown
   */
  createStyleSelector(photo) {
    const wrapper = document.createElement('div');
    wrapper.className = 'style-selector mb-4';

    // Get current style or default to 'hta'
    const currentStyle = photo.render_style || 'hta';

    wrapper.innerHTML = `
      <div class="flex justify-between items-center mb-2">
        <label class="text-sm font-medium text-gray-300">Style</label>
      </div>
      <select 
        id="style-select"
        class="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-gray-200 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 cursor-pointer hover:bg-gray-600 transition-colors"
      >
        <option value="hta" ${currentStyle === 'hta' ? 'selected' : ''}>Style 1</option>
        <option value="inem" ${currentStyle === 'inem' ? 'selected' : ''}>Style 2</option>
      </select>
    `;

    const select = wrapper.querySelector('#style-select');
    select.addEventListener('change', (e) => {
      const newStyle = e.target.value;
      this.updatePhotoStyle(photo, newStyle);
    });

    return wrapper;
  }

  /**
   * Update the photo's render style
   */
  updatePhotoStyle(photo, style) {
    const state = this.editorState.get();
    
    // Update the photo object in memory
    photo.render_style = style;
    
    // Update current photo if it's the same
    if (state.currentPhoto && state.currentPhoto.id === photo.id) {
      state.currentPhoto.render_style = style;
    }

    // Save to backend
    this.saveStyleToBackend(photo, style);
    
    // Notify listeners (e.g., PreviewBox) that settings changed
    document.dispatchEvent(new CustomEvent('photoSettingsChanged', {
      detail: { photo, setting: 'style', value: style }
    }));
    
    // ViewManager will automatically re-render via state listeners
  }

  /**
   * Save style to backend
   */
  async saveStyleToBackend(photo, style) {
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      
      const response = await fetch(`/api/v1/photos/${photo.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          photo: {
            render_style: style
          }
        })
      });

      if (!response.ok) {
        console.error('Failed to save style:', await response.text());
      } else {
        console.log('Style saved successfully for photo', photo.id);
      }
    } catch (error) {
      console.error('Error saving style:', error);
    }
  }

  /**
   * Create a single slider control with value display
   */
  createSliderControl(photo, config) {
    const wrapper = document.createElement('div');
    wrapper.className = 'slider-control mb-4 cursor-pointer';
    wrapper.dataset.sliderName = config.name;

    // Get current value from photo or use default
    const currentValue = photo[config.name] !== undefined ? photo[config.name] : config.default;
    
    // Calculate gradient based on whether 0 is in the middle
    const hasZeroCenter = config.min < 0 && config.max > 0;
    let gradientStyle;
    
    if (hasZeroCenter) {
      const zeroPercent = (0 - config.min) / (config.max - config.min) * 100;
      const valuePercent = (currentValue - config.min) / (config.max - config.min) * 100;
      
      if (currentValue >= 0) {
        // Positive: blue from center to right
        gradientStyle = `linear-gradient(to right, #374151 0%, #374151 ${zeroPercent}%, #3b82f6 ${zeroPercent}%, #3b82f6 ${valuePercent}%, #374151 ${valuePercent}%, #374151 100%)`;
      } else {
        // Negative: blue from left to center
        gradientStyle = `linear-gradient(to right, #374151 0%, #374151 ${valuePercent}%, #3b82f6 ${valuePercent}%, #3b82f6 ${zeroPercent}%, #374151 ${zeroPercent}%, #374151 100%)`;
      }
    } else {
      // Standard gradient from left
      const valuePercent = (currentValue - config.min) / (config.max - config.min) * 100;
      gradientStyle = `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${valuePercent}%, #374151 ${valuePercent}%, #374151 100%)`;
    }

    wrapper.innerHTML = `
      <div class="flex justify-between items-center mb-2">
        <label class="text-sm font-medium text-gray-300 cursor-pointer hover:text-white transition-colors">${config.label}</label>
        <input 
          type="text" 
          class="slider-value-input w-14 px-2 py-1 text-xs text-right bg-transparent border border-transparent rounded text-gray-200 cursor-pointer hover:text-white hover:bg-gray-800/30 focus:outline-none focus:bg-gray-700 focus:border-blue-500 focus:cursor-text transition-all font-mono"
          data-slider="${config.name}"
          value="${currentValue.toFixed(1)}"
          readonly
        />
      </div>
      <div class="relative pb-2">
        <input 
          type="range" 
          class="slider-input w-full h-1.5 bg-gray-700/50 rounded-full appearance-none cursor-pointer relative z-10"
          data-slider="${config.name}"
          min="${config.min}" 
          max="${config.max}" 
          step="${config.step}"
          value="${currentValue}"
          style="background: ${gradientStyle};"
        />
        <div class="absolute top-0.5 left-0 right-0 flex justify-between pointer-events-none" style="padding-left: 7px; padding-right: 7px;">
          <div class="w-px h-2 bg-gray-500/60"></div>
          <div class="w-px h-2 bg-gray-500/60"></div>
          <div class="w-px h-2 bg-gray-500/60"></div>
          <div class="w-px h-2 bg-gray-500/60"></div>
          <div class="w-px h-2 bg-gray-500/60"></div>
        </div>
      </div>
    `;

    const rangeInput = wrapper.querySelector('.slider-input');
    const valueInput = wrapper.querySelector('.slider-value-input');
    const label = wrapper.querySelector('label');

    // Show PreviewBox on mousedown anywhere in the slider control
    wrapper.addEventListener('mousedown', (e) => {
      // Don't show if clicking to edit the value input while it's already focused
      if (e.target === valueInput && document.activeElement === valueInput) {
        return;
      }
      
      // Mark slider as being dragged
      this.editorState.setSliderDragging(true);
      
      // Track the initial value to detect if it changes
      const initialValue = photo[config.name] !== undefined ? photo[config.name] : config.default;
      
      this.previewBox.show(photo, config, wrapper);
      
      // Hide PreviewBox on mouseup (document-level to catch release anywhere)
      // But not if in drawer-always mode
      const mouseUpHandler = (e) => {
        if (this.previewBox.mode !== 'drawer-always') {
          this.previewBox.hide();
        }
        
        // Mark slider as no longer being dragged
        this.editorState.setSliderDragging(false);
        
        // Only dispatch sliderReleased if value actually changed
        const finalValue = photo[config.name] !== undefined ? photo[config.name] : config.default;
        if (finalValue !== initialValue) {
          this.editorState.dispatchEvent(new CustomEvent('sliderReleased', {
            detail: { sliderName: config.name, oldValue: initialValue, newValue: finalValue }
          }));
        }
        
        document.removeEventListener('mouseup', mouseUpHandler);
      };
      
      document.addEventListener('mouseup', mouseUpHandler);
    });

    // Update value display when slider changes
    rangeInput.addEventListener('input', (e) => {
      const value = parseFloat(e.target.value);
      valueInput.value = value.toFixed(1);
      
      // Update gradient fill
      const hasZeroCenter = config.min < 0 && config.max > 0;
      let gradientStyle;
      
      if (hasZeroCenter) {
        const zeroPercent = (0 - config.min) / (config.max - config.min) * 100;
        const valuePercent = (value - config.min) / (config.max - config.min) * 100;
        
        if (value >= 0) {
          gradientStyle = `linear-gradient(to right, #374151 0%, #374151 ${zeroPercent}%, #3b82f6 ${zeroPercent}%, #3b82f6 ${valuePercent}%, #374151 ${valuePercent}%, #374151 100%)`;
        } else {
          gradientStyle = `linear-gradient(to right, #374151 0%, #374151 ${valuePercent}%, #3b82f6 ${valuePercent}%, #3b82f6 ${zeroPercent}%, #374151 ${zeroPercent}%, #374151 100%)`;
        }
      } else {
        const valuePercent = (value - config.min) / (config.max - config.min) * 100;
        gradientStyle = `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${valuePercent}%, #374151 ${valuePercent}%, #374151 100%)`;
      }
      
      e.target.style.background = gradientStyle;
      
      // Update PreviewBox if visible
      if (this.previewBox.isVisible && this.previewBox.activeSliderName === config.name) {
        this.previewBox.updateSliderValue(value);
      }
      
      this.updatePhotoSlider(photo, config.name, value);
    });

    // Make text input editable on click
    valueInput.addEventListener('click', (e) => {
      e.stopPropagation(); // Prevent double-click reset
      e.target.removeAttribute('readonly');
      e.target.select();
    });

    // Make text input readonly again on blur
    valueInput.addEventListener('blur', (e) => {
      e.target.setAttribute('readonly', 'true');
    });

    // Update slider when value input changes
    valueInput.addEventListener('change', (e) => {
      let value = parseFloat(e.target.value);
      
      // Clamp value to min/max
      if (isNaN(value)) value = config.default;
      value = Math.max(config.min, Math.min(config.max, value));
      
      e.target.value = value.toFixed(1);
      rangeInput.value = value;
      
      // Update gradient fill
      const hasZeroCenter = config.min < 0 && config.max > 0;
      let gradientStyle;
      
      if (hasZeroCenter) {
        const zeroPercent = (0 - config.min) / (config.max - config.min) * 100;
        const valuePercent = (value - config.min) / (config.max - config.min) * 100;
        
        if (value >= 0) {
          gradientStyle = `linear-gradient(to right, #374151 0%, #374151 ${zeroPercent}%, #3b82f6 ${zeroPercent}%, #3b82f6 ${valuePercent}%, #374151 ${valuePercent}%, #374151 100%)`;
        } else {
          gradientStyle = `linear-gradient(to right, #374151 0%, #374151 ${valuePercent}%, #3b82f6 ${valuePercent}%, #3b82f6 ${zeroPercent}%, #374151 ${zeroPercent}%, #374151 100%)`;
        }
      } else {
        const valuePercent = (value - config.min) / (config.max - config.min) * 100;
        gradientStyle = `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${valuePercent}%, #374151 ${valuePercent}%, #374151 100%)`;
      }
      
      rangeInput.style.background = gradientStyle;
      
      this.updatePhotoSlider(photo, config.name, value);
    });

    valueInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.target.blur();
      }
    });

    // Double-click anywhere in the control to reset to default
    const resetToDefault = (e) => {
      e.preventDefault();
      const defaultValue = config.default;
      valueInput.value = defaultValue.toFixed(1);
      rangeInput.value = defaultValue;
      
      // Update gradient fill
      const hasZeroCenter = config.min < 0 && config.max > 0;
      let gradientStyle;
      
      if (hasZeroCenter) {
        const zeroPercent = (0 - config.min) / (config.max - config.min) * 100;
        const valuePercent = (defaultValue - config.min) / (config.max - config.min) * 100;
        
        if (defaultValue >= 0) {
          gradientStyle = `linear-gradient(to right, #374151 0%, #374151 ${zeroPercent}%, #3b82f6 ${zeroPercent}%, #3b82f6 ${valuePercent}%, #374151 ${valuePercent}%, #374151 100%)`;
        } else {
          gradientStyle = `linear-gradient(to right, #374151 0%, #374151 ${valuePercent}%, #3b82f6 ${valuePercent}%, #3b82f6 ${zeroPercent}%, #374151 ${zeroPercent}%, #374151 100%)`;
        }
      } else {
        const valuePercent = (defaultValue - config.min) / (config.max - config.min) * 100;
        gradientStyle = `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${valuePercent}%, #374151 ${valuePercent}%, #374151 100%)`;
      }
      
      rangeInput.style.background = gradientStyle;
      
      this.updatePhotoSlider(photo, config.name, defaultValue);
    };

    wrapper.addEventListener('dblclick', resetToDefault);
    label.addEventListener('dblclick', resetToDefault);
    rangeInput.addEventListener('dblclick', resetToDefault);

    // PREVIEW BAR CODE REMOVED - Now using PreviewBox on mousedown
    // // Add click handler for preview bar
    // wrapper.addEventListener('click', (e) => {
    //   // Don't trigger if clicking on the range input (let it handle its own events)
    //   if (e.target === rangeInput) return;
    //   
    //   this.showPreviewBar(photo, config.name);
    // });

    return wrapper;
  }

  /**
   * Update a slider value on the photo (debounced save to backend)
   */
  updatePhotoSlider(photo, sliderName, value) {
    // Update the photo object in memory (for backend save)
    photo[sliderName] = value;
    
    // Update reactive state (this will trigger PreviewBox and other listeners)
    this.editorState.setSlider(sliderName, value);

    // Debounced save to backend
    this.debouncedSave(photo);
  }

  /**
   * Debounced save to backend (500ms delay)
   */
  debouncedSave(photo) {
    const photoId = photo.id;
    
    // Clear existing timer for this photo
    if (this.saveDebounceTimers[photoId]) {
      clearTimeout(this.saveDebounceTimers[photoId]);
    }

    // Set new timer
    this.saveDebounceTimers[photoId] = setTimeout(() => {
      this.saveSliderValues(photo);
      delete this.saveDebounceTimers[photoId];
    }, 500);
  }

  /**
   * Save slider values to backend
   */
  async saveSliderValues(photo) {
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      
      const sliderData = {};
      this.sliders.forEach(config => {
        sliderData[config.name] = photo[config.name] !== undefined ? photo[config.name] : config.default;
      });

      const response = await fetch(`/api/v1/photos/${photo.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          photo: sliderData
        })
      });

      if (!response.ok) {
        console.error('Failed to save slider values:', await response.text());
      } else {
        console.log('Slider values saved successfully for photo', photo.id);
      }
    } catch (error) {
      console.error('Error saving slider values:', error);
    }
  }

  /**
   * Get slider value by name
   */
  getSliderValue(photo, sliderName) {
    const config = this.sliders.find(s => s.name === sliderName);
    if (!config) return 0;
    return photo[sliderName] !== undefined ? photo[sliderName] : config.default;
  }

  /**
   * Set slider value by name and update UI
   */
  setSliderValue(photo, sliderName, value) {
    const config = this.sliders.find(s => s.name === sliderName);
    if (!config) return;

    // Clamp value
    value = Math.max(config.min, Math.min(config.max, value));

    // Update photo
    this.updatePhotoSlider(photo, sliderName, value);

    // Update UI
    const rangeInput = document.querySelector(`.slider-input[data-slider="${sliderName}"]`);
    const valueInput = document.querySelector(`.slider-value-input[data-slider="${sliderName}"]`);

    if (rangeInput) rangeInput.value = value;
    if (valueInput) valueInput.value = value.toFixed(1);
  }

  /**
   * Reset all sliders to default
   */
  resetAllSliders(photo) {
    this.sliders.forEach(config => {
      this.setSliderValue(photo, config.name, config.default);
    });
  }

  /**
   * Adjust focused slider value
   */
  adjustFocusedSlider(delta) {
    const focusedInput = document.activeElement;
    
    if (focusedInput && focusedInput.classList.contains('slider-input')) {
      const sliderName = focusedInput.dataset.slider;
      const config = this.sliders.find(s => s.name === sliderName);
      
      if (config) {
        const state = this.editorState.get();
        const photo = state.currentPhoto;
        
        if (photo) {
          const currentValue = this.getSliderValue(photo, sliderName);
          const newValue = currentValue + delta;
          this.setSliderValue(photo, sliderName, newValue);
        }
      }
    }
  }

  /**
   * Focus slider by index (for keyboard shortcuts)
   */
  focusSlider(index) {
    const sliders = document.querySelectorAll('.slider-input');
    if (sliders[index]) {
      sliders[index].focus();
      sliders[index].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  // PREVIEW BAR CODE - COMMENTED OUT (Now using PreviewBox)
  // /**
  //  * Show preview bar for a specific slider
  //  */
  // showPreviewBar(photo, sliderName) {
  //   const wasAlreadyOpen = this.activeSlider === sliderName && this.previewBar;
  //   this.activeSlider = sliderName;
  //
  //   // If bar doesn't exist yet, create it
  //   if (!this.previewBar) {
  //     this.createPreviewBar();
  //   }
  //
  //   // Open the bar if it's closed
  //   if (!this.previewBarOpen) {
  //     this.previewBar.style.transform = 'translateY(0)';
  //     this.previewBarOpen = true;
  //     this.updateToggleTabArrow();
  //   }
  //
  //   // Render the preview variants for this slider
  //   this.renderPreviewVariants(photo, sliderName, !wasAlreadyOpen);
  // }

  // /**
  //  * Create the preview bar container
  //  */
  // createPreviewBar() {
  //   const editPage = document.getElementById('edit-page');
  //   if (!editPage) return;
  //
  //   // Create the preview bar at the bottom of the photo area
  //   const previewBar = document.createElement('div');
  //   previewBar.id = 'slider-preview-bar';
  //   previewBar.className = 'absolute bottom-0 left-0 right-0 bg-gray-900/95 border-t border-gray-700 p-4 z-40 transform transition-transform duration-300 ease-out';
  //   previewBar.style.transform = 'translateY(100%)';
  //
  //   previewBar.innerHTML = `
  //     <div id="preview-variants" class="flex gap-3 justify-between overflow-x-auto">
  //       <!-- Preview variants will be added here -->
  //     </div>
  //   `;
  //
  //   // Create the toggle tab that sticks out - as a child of previewBar so it moves with it
  //   const toggleTab = document.createElement('button');
  //   toggleTab.id = 'preview-bar-toggle-tab';
  //   toggleTab.className = 'absolute -top-10 right-4 bg-gray-900/95 border border-gray-700 border-b-0 rounded-t-lg px-3 py-2 text-gray-400 hover:text-white transition-colors z-10 cursor-pointer';
  //   toggleTab.innerHTML = `
  //     <svg id="toggle-arrow-up" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  //       <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7"></path>
  //     </svg>
  //     <svg id="toggle-arrow-down" class="w-5 h-5 hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  //       <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
  //     </svg>
  //   `;
  //
  //   // Add toggle tab to preview bar (so it moves with the bar)
  //   previewBar.appendChild(toggleTab);
  //
  //   // Add to the main image view (first child of edit page flex container)
  //   const mainImageView = editPage.querySelector('.flex-1.flex.items-center.justify-center.bg-black');
  //   if (mainImageView) {
  //     mainImageView.style.position = 'relative';
  //     mainImageView.appendChild(previewBar);
  //   }
  //
  //   // Add toggle tab handler
  //   toggleTab.addEventListener('click', () => {
  //     this.togglePreviewBar();
  //   });
  //
  //   this.previewBar = previewBar;
  //   this.previewBarToggleTab = toggleTab;
  //   this.previewBarOpen = false;
  //   
  //   // Update arrow to show up arrow (since bar starts closed)
  //   this.updateToggleTabArrow();
  // }

  // /**
  //  * Toggle the preview bar open/closed
  //  */
  // togglePreviewBar() {
  //   if (!this.previewBar) return;
  //
  //   if (this.previewBarOpen) {
  //     // Close it
  //     this.previewBar.style.transform = 'translateY(100%)';
  //     this.previewBarOpen = false;
  //   } else {
  //     // Open it
  //     this.previewBar.style.transform = 'translateY(0)';
  //     this.previewBarOpen = true;
  //   }
  //
  //   this.updateToggleTabArrow();
  // }
  //
  // /**
  //  * Update the toggle tab arrow based on open/closed state
  //  */
  // updateToggleTabArrow() {
  //   if (!this.previewBarToggleTab) return;
  //
  //   const upArrow = this.previewBarToggleTab.querySelector('#toggle-arrow-up');
  //   const downArrow = this.previewBarToggleTab.querySelector('#toggle-arrow-down');
  //
  //   if (this.previewBarOpen) {
  //     // Bar is up, show down arrow
  //     upArrow.classList.add('hidden');
  //     downArrow.classList.remove('hidden');
  //   } else {
  //     // Bar is down, show up arrow
  //     upArrow.classList.remove('hidden');
  //     downArrow.classList.add('hidden');
  //   }
  // }
  //
  // /**
  //  * Close the preview bar (slide down but don't destroy)
  //  */
  // closePreviewBar() {
  //   if (!this.previewBar) return;
  //
  //   // Animate out
  //   this.previewBar.style.transform = 'translateY(100%)';
  //   this.previewBarOpen = false;
  //   this.updateToggleTabArrow();
  //
  //   // Clean up PhotoRender instances
  //   this.photoRenders.forEach(render => render.destroy());
  //   this.photoRenders = [];
  //
  //   this.activeSlider = null;
  // }
  
  /**
   * Completely remove the preview bar (for cleanup when leaving edit page)
   */
  destroyPreviewBar() {
    if (!this.previewBar) return;

    // Clean up PhotoRender instances
    this.photoRenders.forEach(render => render.destroy());
    this.photoRenders = [];

    // Remove preview bar (toggle tab is a child, so it will be removed too)
    if (this.previewBar) {
      this.previewBar.remove();
      this.previewBar = null;
    }
    this.previewBarToggleTab = null;

    this.activeSlider = null;
    this.previewBarOpen = false;
  }
  
  // OLD RENDERING CODE - Now handled by ViewManager's PhotoRender instance
  // ViewManager listens to state changes and automatically triggers renders
  // Keeping this commented out for reference
  /**
   * Render the main photo at 1024px with current settings
   * DEPRECATED: Now handled by ViewManager
   */
  // renderMainPhoto(photo) {
  //   // ViewManager now handles all main photo rendering via its PhotoRender instance
  //   // which listens to editorState changes automatically
  // }
  
  /**
   * Set view manager reference
   */
  setViewManager(viewManager) {
    this.viewManager = viewManager;
  }
  
  /**
   * Show loading overlay on main photo
   */
  showMainPhotoLoadingOverlay() {
    // Check if overlay already exists
    let overlay = document.getElementById('main-photo-loading-overlay');
    if (overlay) {
      overlay.classList.remove('hidden');
      return;
    }
    
    // Create overlay
    overlay = document.createElement('div');
    overlay.id = 'main-photo-loading-overlay';
    overlay.className = 'absolute inset-0 bg-black/70 flex items-center justify-center z-20';
    overlay.innerHTML = `
      <div class="text-center">
        <svg class="animate-spin h-12 w-12 mx-auto mb-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <p class="text-white text-sm">Rendering...</p>
      </div>
    `;
    
    // Add to main image container (same parent as edit-image)
    const editImage = document.getElementById('edit-image');
    if (editImage && editImage.parentElement) {
      editImage.parentElement.style.position = 'relative';
      editImage.parentElement.appendChild(overlay);
    }
  }
  
  /**
   * Hide loading overlay
   */
  hideMainPhotoLoadingOverlay() {
    const overlay = document.getElementById('main-photo-loading-overlay');
    if (overlay) {
      overlay.classList.add('hidden');
      // Don't remove, just hide for reuse
    }
  }
  
  /**
   * Show error message on main photo
   */
  showMainPhotoError(message) {
    let overlay = document.getElementById('main-photo-loading-overlay');
    if (overlay) {
      overlay.innerHTML = `
        <div class="text-center">
          <svg class="w-16 h-16 mx-auto mb-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <p class="text-red-400 mb-4">${message}</p>
          <button class="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600" onclick="document.getElementById('main-photo-loading-overlay').classList.add('hidden')">
            Close
          </button>
        </div>
      `;
      overlay.classList.remove('hidden');
    }
  }
  //
  // /**
  //  * Render preview variants for a specific slider
  //  */
  // async renderPreviewVariants(photo, sliderName, shouldAnimate = true) {
  //   const variantsContainer = document.getElementById('preview-variants');
  //   if (!variantsContainer) return;
  //
  //   // Clean up existing PhotoRenders
  //   this.photoRenders.forEach(render => render.destroy());
  //   this.photoRenders = [];
  //
  //   // Clear container
  //   variantsContainer.innerHTML = '';
  //
  //   // Get image aspect ratio from the main edit image
  //   const editImage = document.getElementById('edit-image');
  //   let aspectRatio = 1;
  //   if (editImage && editImage.naturalWidth && editImage.naturalHeight) {
  //     aspectRatio = editImage.naturalWidth / editImage.naturalHeight;
  //   }
  //
  //   // Calculate preview dimensions (height is fixed, width adapts to aspect ratio)
  //   const previewHeight = 180;
  //   const previewWidth = previewHeight * aspectRatio;
  //
  //   // Generate 5 variants: 0%, 25%, 50%, 75%, 100%
  //   const percentages = [0, 0.25, 0.5, 0.75, 1.0];
  //   const config = this.sliders.find(s => s.name === sliderName);
  //   if (!config) return;
  //
  //   percentages.forEach((percentage, index) => {
  //     // Calculate the slider value for this percentage
  //     const sliderValue = config.min + (config.max - config.min) * percentage;
  //
  //     // Create container for this variant
  //     const variantDiv = document.createElement('div');
  //     variantDiv.className = 'flex-1 min-w-0';
  //     variantDiv.innerHTML = `
  //       <div class="relative rounded-lg overflow-hidden border-2 border-gray-700 hover:border-blue-500 transition-colors cursor-pointer" style="aspect-ratio: ${aspectRatio}; height: ${previewHeight}px;">
  //         <div class="preview-render-container w-full h-full"></div>
  //       </div>
  //       <div class="text-center mt-2 text-xs text-gray-400">${sliderValue.toFixed(1)}</div>
  //     `;
  //
  //     const renderContainer = variantDiv.querySelector('.preview-render-container');
  //
  //     // Import PhotoRender dynamically
  //     import('./photo_render.js').then(({ PhotoRender }) => {
  //       // Get render parameters
  //       const renderParams = this.getRenderParams(photo, sliderName, sliderValue);
  //
  //       // Get the selected style from photo or default to 'hta'
  //       const selectedStyle = photo.render_style || 'hta';
  //
  //       // Create PhotoRender instance
  //       const photoRender = new PhotoRender(renderContainer, {
  //         photoId: photo.id,
  //         style: selectedStyle,
  //         resolution: 512,
  //         ...renderParams,
  //         onProgress: (count, imageUrl) => {
  //           // Progress callback
  //         },
  //         onComplete: (images) => {
  //           // console.log(`Preview ${index} complete: ${images.length} images`);
  //         },
  //         onError: (error) => {
  //           console.error(`Preview ${index} error:`, error);
  //         }
  //       });
  //
  //       this.photoRenders.push(photoRender);
  //
  //       // Add click handler to jump slider to this value
  //       variantDiv.addEventListener('click', () => {
  //         this.setSliderValue(photo, sliderName, sliderValue);
  //       });
  //     });
  //
  //     variantsContainer.appendChild(variantDiv);
  //   });
  // }

  /**
   * Get render parameters for a specific slider value
   * (Delegates to slider_mappings.js for consistency)
   */
  getRenderParams(photo, activeSliderName, activeSliderValue) {
    const state = this.editorState.get();
    return getRenderParams(photo, activeSliderName, activeSliderValue, state.inferenceSteps, state.seed, state.sampleEvery);
  }

  /**
   * Update inference steps and trigger re-render
   */
  updateInferenceSteps(photo, value) {
    // Update reactive state
    this.editorState.set({ inferenceSteps: value });
    
    // Update photo object in memory (for backend save)
    photo.inference_steps = value;

    // Debounced save and re-render
    this.debouncedUpdateRenderSettings(photo);
    
    // Notify listeners (e.g., PreviewBox) that settings changed
    document.dispatchEvent(new CustomEvent('photoSettingsChanged', {
      detail: { photo, setting: 'inferenceSteps', value }
    }));
  }

  /**
   * Update seed and trigger re-render
   */
  updateSeed(photo, value) {
    // Update reactive state
    this.editorState.set({ seed: value });
    
    // Update photo object in memory (for backend save)
    photo.seed = value;

    // Debounced save and re-render
    this.debouncedUpdateRenderSettings(photo);
    
    // Notify listeners (e.g., PreviewBox) that settings changed
    document.dispatchEvent(new CustomEvent('photoSettingsChanged', {
      detail: { photo, setting: 'seed', value }
    }));
  }

  /**
   * Debounced update for render settings (500ms delay)
   */
  debouncedUpdateRenderSettings(photo) {
    // Clear existing timer
    if (this.renderSettingsDebounceTimer) {
      clearTimeout(this.renderSettingsDebounceTimer);
    }

    // Set new timer
    this.renderSettingsDebounceTimer = setTimeout(() => {
      this.saveRenderSettings(photo);
      // this.updateAllPhotoRenders(); // REMOVED - preview bar no longer used
      this.renderSettingsDebounceTimer = null;
    }, 500);
  }

  /**
   * Save render settings to backend
   */
  async saveRenderSettings(photo) {
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      
      const response = await fetch(`/api/v1/photos/${photo.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          photo: {
            inference_steps: photo.inference_steps,
            seed: photo.seed
          }
        })
      });

      if (!response.ok) {
        console.error('Failed to save render settings:', await response.text());
      } else {
        console.log('Render settings saved successfully for photo', photo.id);
      }
    } catch (error) {
      console.error('Error saving render settings:', error);
    }
  }

  // /**
  //  * Update all active PhotoRender instances with new settings
  //  */
  // updateAllPhotoRenders() {
  //   const state = this.editorState.get();
  //   const photo = state.currentPhoto;
  //   
  //   if (!photo) return;
  //
  //   // Update all preview PhotoRenders
  //   this.photoRenders.forEach(photoRender => {
  //     photoRender.updateParams({
  //       numInferenceSteps: this.inferenceSteps,
  //       seed: this.seed
  //     });
  //   });
  // }
}

